import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InvoicePopup } from '../components/sessions';
import { toast } from 'sonner';
import { 
  UserCircle, Search, Eye, Ban, CheckCircle, 
  Wallet, Zap, Activity, Mail, Phone, Calendar,
  TrendingUp, Clock, Key, Smartphone, Scan,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  Building2, User, ShieldCheck, ShieldX, BarChart3,
  Users, CreditCard, PieChart, MoreHorizontal, IdCard, Trash2,
  AlertTriangle, FileText, Image, Car, Banknote, X, ExternalLink, UserPlus, Lock, Link2
  , Download
} from 'lucide-react';
import api, { getBusinessEmployeesList } from '../lib/api';
import { formatDate, formatCurrency as formatMoney, extractApiErrorMessage } from '../lib/utils';
import { saveElementAsPdf } from '../lib/pdf';

const ITEMS_PER_PAGE = 25;
const REPORT_REFRESH_INTERVAL = 30000; // 30 seconds

const COMPANY_INFO = {
  name: 'C.S.M Sh.p.k.',
  address: 'Tirane Vore Muçaj Rruga dytesore, Tirane -Vore, 1032, Vore, Tirane',
  city: 'Tiranë, Shqipëri',
  nipt: 'L91234567A',
  phone: '+355 68 200 1818',
  email: 'info@albmove.al',
};

const getWalletSummary = (client) => {
  const summary = client?.wallet_summary || {};
  const total = Number(client?.wallet_balance ?? summary.total_balance ?? 0);
  const globalBalance = Number(summary.global_balance ?? total ?? 0);
  const stationBalance = Number(summary.station_balance ?? 0);
  const restrictedWallets = Array.isArray(summary.restricted_wallets) ? summary.restricted_wallets : [];

  return {
    total,
    globalBalance,
    stationBalance,
    restrictedWallets,
  };
};

const getClientModeratorIds = (client) => {
  const ids = [];
  if (Array.isArray(client?.moderator_ids)) {
    client.moderator_ids.forEach((id) => {
      if (id && !ids.includes(id)) ids.push(id);
    });
  }
  if (client?.moderator_id && !ids.includes(client.moderator_id)) {
    ids.push(client.moderator_id);
  }
  return ids;
};

const getClientModeratorNames = (client, moderatorsList = []) => {
  const ids = getClientModeratorIds(client);
  if (!ids.length) return [];

  const names = ids
    .map((id) => moderatorsList.find((mod) => mod.id === id)?.full_name)
    .filter(Boolean);

  if (names.length) return names;
  if (Array.isArray(client?.moderators)) {
    return client.moderators.map((mod) => mod.full_name).filter(Boolean);
  }
  if (client?.moderator_name) return [client.moderator_name];
  return [];
};

const Clients = () => {
  const formatCurrency = (amount, currency = 'Kredite') => formatMoney(amount, currency);
  const { user } = useAuth();
  const isBusiness = user?.role === 'BUSINESS';
  
  // Set page title based on role
  usePageTitle(isBusiness ? 'Punonjësit' : 'Klientët');
  
  const isModerator = user?.role === 'MODERATOR';
  const canAssignModerator = ['ADMIN', 'SUPER_ADMIN'].includes(user?.role);
  const canManageDiscount = ['ADMIN', 'SUPER_ADMIN', 'MODERATOR'].includes(user?.role);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Report state - always visible
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const reportIntervalRef = useRef(null);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientSessions, setClientSessions] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [discountDraft, setDiscountDraft] = useState('');
  const [discountFixedDraft, setDiscountFixedDraft] = useState('');
  const [discountSaving, setDiscountSaving] = useState(false);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const invoiceRef = useRef(null);

  // Password reset dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email change dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Cash topup dialog
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNotes, setTopupNotes] = useState('');
  const [topupSiteId, setTopupSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [topupLoading, setTopupLoading] = useState(false);

  // Moderator assignment dialog
  const [moderators, setModerators] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignClientTarget, setAssignClientTarget] = useState(null);
  const [selectedModeratorIds, setSelectedModeratorIds] = useState([]);
  const [assignModeratorLoading, setAssignModeratorLoading] = useState(false);

  // Create client dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    full_name: '', email: '', phone: '', password: '', account_type: 'individual'
  });

  const handleCreateClient = async () => {
    if (!createForm.full_name || !createForm.email || !createForm.password) {
      toast.error('Emri, email dhe fjalëkalimi janë të detyrueshme');
      return;
    }
    try {
      setCreateLoading(true);
      
      // Business users use different endpoint
      if (isBusiness) {
        await api.post('/v1/business/employees/create', {
          full_name: createForm.full_name,
          email: createForm.email,
          password: createForm.password,
          phone: createForm.phone
        });
        toast.success('Punonjësi u krijua me sukses');
      } else {
        // Admin/Moderator use standard endpoint
        await api.post('/admin/clients', createForm);
        toast.success('Klienti u krijua me sukses');
      }
      
      setCreateDialogOpen(false);
      setCreateForm({ full_name: '', email: '', phone: '', password: '', account_type: 'individual' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë krijimit');
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // BUSINESS users fetch their employees
      if (user?.role === 'BUSINESS') {
        if (searchQuery) params.append('search', searchQuery);
        params.append('skip', ((currentPage - 1) * ITEMS_PER_PAGE).toString());
        params.append('limit', ITEMS_PER_PAGE.toString());
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
        
        const response = await getBusinessEmployeesList();
        const employeeItems = response?.data?.employees || [];
        const filtered = searchQuery
          ? employeeItems.filter(emp =>
              String(emp?.employee_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
              String(emp?.employee_email || '').toLowerCase().includes(searchQuery.toLowerCase())
            )
          : employeeItems;
        const mapped = filtered.map((emp) => {
          const total = Number(emp?.wallet_summary?.total_balance ?? emp?.wallet_balance ?? 0);
          const global_b = Number(emp?.wallet_summary?.global_balance ?? 0);
          const station_b = Number(emp?.wallet_summary?.station_balance ?? 0);
          const rw = Array.isArray(emp?.wallet_summary?.restricted_wallets) ? emp.wallet_summary.restricted_wallets : [];
          return {
            ...emp,
            id: emp.id,
            employee_record_id: emp.id,
            client_id: emp.employee_account_id || emp.employee_id,
            full_name: emp.employee_name,
            email: emp.employee_email,
            phone: emp.employee_phone || null,
            status: emp.status || 'active',
            account_type: 'individual',
            is_verified: true,
            wallet_balance: total,
            wallet_summary: {
              total_balance: total,
              global_balance: global_b,
              station_balance: station_b,
              restricted_wallets: rw,
            },
          };
        });
        setClients(mapped);
        setTotal(mapped.length || 0);
        setTotalPages(1);
      } else {
        // Admin/Moderator fetch clients
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (verifiedFilter !== 'all') params.append('is_verified', verifiedFilter === 'verified' ? 'true' : 'false');
        if (searchQuery) params.append('search', searchQuery);
        params.append('skip', ((currentPage - 1) * ITEMS_PER_PAGE).toString());
        params.append('limit', ITEMS_PER_PAGE.toString());
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
        
        const response = await api.get(`/admin/clients?${params.toString()}`);
        setClients(response.data.items || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      }
    } catch (error) {
      toast.error('Gabim në marrjen e klientëve');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, verifiedFilter, searchQuery, currentPage, sortBy, sortOrder, user?.role]);

  const fetchReport = useCallback(async () => {
    try {
      setReportLoading(true);
      const response = await api.get('/admin/clients/stats/report');
      setReport(response.data);
    } catch (error) {
      console.error('Report fetch error:', error);
    } finally {
      setReportLoading(false);
    }
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites');
      const siteItems = Array.isArray(response.data) ? response.data : response.data?.sites || [];
      const sortedSites = [...siteItems].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sq'));
      setSites(sortedSites);
      setTopupSiteId((prev) => prev || (sortedSites.length === 1 ? sortedSites[0].id : ''));
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  }, []);

  const fetchModerators = useCallback(async () => {
    if (!canAssignModerator || isBusiness) {
      setModerators([]);
      return;
    }

    try {
      const response = await api.get('/moderators', {
        params: { skip: 0, limit: 500 },
      });
      setModerators(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch moderators:', error);
    }
  }, [canAssignModerator, isBusiness]);

  // Auto-refresh report
  useEffect(() => {
    fetchReport(); // Initial fetch
    
    reportIntervalRef.current = setInterval(() => {
      fetchReport();
    }, REPORT_REFRESH_INTERVAL);
    
    return () => {
      if (reportIntervalRef.current) {
        clearInterval(reportIntervalRef.current);
      }
    };
  }, [fetchReport]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchClients]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchModerators();
  }, [fetchModerators]);

  const fetchClientDetails = async (clientId) => {
    try {
      setDetailsLoading(true);
      if (isBusiness) {
        const detailsRes = await api.get(`/v1/business/employees/${clientId}`);
        const emp = detailsRes?.data?.employee || {};

        const restrictedWallets = emp?.wallet_summary?.restricted_wallets || [];
        // Use total_balance from backend directly (authoritative: clients.wallet_balance)
        const totalBalance = Number(emp?.wallet_summary?.total_balance ?? 0);
        const stationBalance = Number(emp?.wallet_summary?.station_balance ?? 0);
        const globalBalance = Number(emp?.wallet_summary?.global_balance ?? Math.max(0, totalBalance - stationBalance));

        const detailsData = {
          id: emp.employee_account_id || emp.employee_id || clientId,
          employee_record_id: emp.id || clientId,
          _employee_record_id: clientId,
          full_name: emp.employee_name || selectedClient?.full_name,
          email: emp.employee_email || selectedClient?.email,
          phone: emp.employee_phone || selectedClient?.phone,
          status: emp.status || 'active',
          account_type: 'individual',
          wallet_balance: totalBalance,
          wallet_summary: {
            total_balance: totalBalance,
            global_balance: globalBalance,
            station_balance: stationBalance,
            restricted_wallets: restrictedWallets
          },
          transactions: Array.isArray(emp.transactions) ? emp.transactions : []
        };

        let employeeInvoices = [];
        try {
          const invoicesRes = await api.get('/invoices', {
            params: {
              user_id: detailsData.id,
              skip: 0,
              limit: 500,
            },
          });
          employeeInvoices = Array.isArray(invoicesRes?.data)
            ? invoicesRes.data
            : Array.isArray(invoicesRes?.data?.items)
              ? invoicesRes.data.items
              : [];
        } catch (invoiceError) {
          employeeInvoices = [];
        }

        setClientDetails(detailsData);
        setClientSessions(detailsData.transactions || []);
        setClientInvoices(employeeInvoices);
        setDiscountDraft('');
        setDiscountFixedDraft('');
        return;
      }

      const [detailsRes, sessionsRes, invoicesRes] = await Promise.allSettled([
        api.get(`/admin/clients/${clientId}`),
        api.get(`/clients/${clientId}/sessions?skip=0&limit=500`),
        api.get(`/admin/clients/${clientId}/invoices?skip=0&limit=500`)
      ]);

      if (detailsRes.status !== 'fulfilled') {
        throw detailsRes.reason;
      }

      const detailsData = detailsRes.value.data;
      setClientDetails(detailsData);
      setDiscountDraft(
        detailsData?.charging_discount_percent !== undefined && detailsData?.charging_discount_percent !== null
          ? String(detailsData.charging_discount_percent)
          : ''
      );
      setDiscountFixedDraft(
        detailsData?.charging_discount_fixed_lek !== undefined && detailsData?.charging_discount_fixed_lek !== null
          ? String(detailsData.charging_discount_fixed_lek)
          : ''
      );

      if (sessionsRes.status === 'fulfilled') {
        const sessionItems = sessionsRes.value.data?.items || sessionsRes.value.data || [];
        setClientSessions(Array.isArray(sessionItems) ? sessionItems : []);
      } else {
        setClientSessions(detailsData?.recent_sessions || []);
      }

      if (invoicesRes.status === 'fulfilled') {
        const invoiceItems = invoicesRes.value.data?.invoices || invoicesRes.value.data || [];
        setClientInvoices(Array.isArray(invoiceItems) ? invoiceItems : []);
      } else {
        setClientInvoices(detailsData?.invoices || []);
      }
    } catch (error) {
      toast.error('Gabim në marrjen e detajeve të klientit');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSaveClientDiscount = async () => {
    if (!clientDetails?.id) return;

    const trimmed = String(discountDraft || '').trim();
    const fixedTrimmed = String(discountFixedDraft || '').trim();
    let discountPercent = null;
    let discountFixedLek = null;

    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        toast.error('Zbritja duhet të jetë numër');
        return;
      }
      if (parsed < 0 || parsed > 100) {
        toast.error('Zbritja duhet të jetë nga 0 deri në 100%');
        return;
      }
      discountPercent = Number(parsed.toFixed(2));
    }

    if (fixedTrimmed !== '') {
      const parsedFixed = Number(fixedTrimmed);
      if (!Number.isFinite(parsedFixed)) {
        toast.error('Zbritja fikse duhet të jetë numër');
        return;
      }
      if (parsedFixed < 0) {
        toast.error('Zbritja fikse duhet të jetë 0 ose më e madhe');
        return;
      }
      discountFixedLek = Number(parsedFixed.toFixed(2));
    }

    try {
      setDiscountSaving(true);
      await api.put('/settings/client-discount', {
        client_id: clientDetails.id,
        discount_percent: discountPercent,
        discount_fixed_lek: discountFixedLek,
      });

      const nextPercent = discountPercent;
      const nextFixed = discountFixedLek;
      setClientDetails(prev => prev ? {
        ...prev,
        charging_discount_percent: nextPercent,
        charging_discount_fixed_lek: nextFixed,
      } : prev);
      setClients(prev => prev.map(c => c.id === clientDetails.id ? {
        ...c,
        charging_discount_percent: nextPercent,
        charging_discount_fixed_lek: nextFixed,
      } : c));
      setDiscountDraft(nextPercent === null ? '' : String(nextPercent));
      setDiscountFixedDraft(nextFixed === null ? '' : String(nextFixed));

      toast.success(nextPercent === null && nextFixed === null ? 'Zbritja u hoq me sukses' : 'Zbritja u ruajt me sukses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ruajtjen e zbritjes');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleViewDetails = async (client) => {
    setSelectedClient(client);
    setDetailDialogOpen(true);
    await fetchClientDetails(client.id);
  };

  const handleBlock = async (clientId) => {
    if (!window.confirm('A jeni të sigurt që dëshironi ta bllokoni këtë klient?')) return;
    
    try {
      await api.post(`/admin/clients/${clientId}/block`);
      toast.success('Klienti u bllokua');
      fetchClients();
      fetchReport();
      if (clientDetails?.id === clientId) {
        setClientDetails({ ...clientDetails, status: 'blocked' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const handleUnblock = async (clientId) => {
    try {
      await api.post(`/admin/clients/${clientId}/unblock`);
      toast.success('Klienti u zhbllokua');
      fetchClients();
      fetchReport();
      if (clientDetails?.id === clientId) {
        setClientDetails({ ...clientDetails, status: 'active' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const openDeleteDialog = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    
    try {
      setDeleteLoading(true);
      await api.delete(`/admin/clients/${clientToDelete.id}`);
      toast.success('Klienti u fshi me sukses');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
      fetchReport();
      // Close details dialog if open
      if (clientDetails?.id === clientToDelete.id) {
        setDetailDialogOpen(false);
        setClientDetails(null);
        setClientSessions([]);
        setClientInvoices([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në fshirjen e klientit');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    
    try {
      setPasswordLoading(true);
      await api.post(`/admin/clients/${selectedClient.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Fjalëkalimi u ndryshua me sukses');
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ndryshimin e fjalëkalimit');
    } finally {
      setPasswordLoading(false);
    }
  };

  const openPasswordDialog = (client) => {
    setSelectedClient(client);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const openEmailDialog = (client) => {
    setSelectedClient(client);
    setNewEmail(client?.email || '');
    setEmailDialogOpen(true);
  };

  const handleChangeEmail = async () => {
    const trimmed = (newEmail || '').trim().toLowerCase();
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(trimmed)) {
      toast.error('Vendos një email të vlefshëm');
      return;
    }
    if (!selectedClient?.id) return;

    try {
      setEmailLoading(true);
      await api.post(`/admin/clients/${selectedClient.id}/change-email`, {
        new_email: trimmed,
      });
      toast.success('Email-i u ndryshua me sukses');
      setClients((prev) => prev.map((c) => (
        c.id === selectedClient.id ? { ...c, email: trimmed } : c
      )));
      if (clientDetails?.id === selectedClient.id) {
        setClientDetails((prev) => (prev ? { ...prev, email: trimmed } : prev));
      }
      setSelectedClient((prev) => (prev ? { ...prev, email: trimmed } : prev));
      setEmailDialogOpen(false);
      setNewEmail('');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në ndryshimin e email-it'));
    } finally {
      setEmailLoading(false);
    }
  };

  const openTopupDialog = (client) => {
    setSelectedClient(client);
    setTopupAmount('');
    setTopupNotes('');
    setTopupSiteId((prev) => prev || (sites.length === 1 ? sites[0].id : ''));
    setTopupDialogOpen(true);
  };

  const openAssignModeratorDialog = (client) => {
    setAssignClientTarget(client);
    const assignedIds = getClientModeratorIds(client).filter((id) =>
      moderators.some((mod) => mod.id === id)
    );
    setSelectedModeratorIds(assignedIds);
    setAssignDialogOpen(true);
  };

  const toggleModeratorSelection = (moderatorId) => {
    setSelectedModeratorIds((prev) =>
      prev.includes(moderatorId)
        ? prev.filter((id) => id !== moderatorId)
        : [...prev, moderatorId]
    );
  };

  const handleAssignModerator = async () => {
    if (!assignClientTarget?.id) return;

    try {
      setAssignModeratorLoading(true);
      const response = await api.post(`/admin/clients/${assignClientTarget.id}/assign-moderator`, {
        moderator_ids: selectedModeratorIds,
      });

      const assignedModerators = response.data?.moderators || selectedModeratorIds
        .map((id) => moderators.find((mod) => mod.id === id))
        .filter(Boolean);

      setClients((prev) => prev.map((client) => {
        if (client.id !== assignClientTarget.id) return client;
        return {
          ...client,
          moderator_ids: selectedModeratorIds,
          moderator_id: selectedModeratorIds[0] || null,
          moderator_name: assignedModerators[0]?.full_name || null,
          moderators: assignedModerators,
        };
      }));

      if (clientDetails?.id === assignClientTarget.id) {
        setClientDetails((prev) => (prev ? {
          ...prev,
          moderator_ids: selectedModeratorIds,
          moderator_id: selectedModeratorIds[0] || null,
          moderator_name: assignedModerators[0]?.full_name || null,
          moderators: assignedModerators,
        } : prev));
      }

      toast.success(
        response.data?.message ||
          (selectedModeratorIds.length
            ? 'Moderatorët u caktuan me sukses'
            : 'Lidhja me moderatorët u hoq me sukses')
      );

      setAssignDialogOpen(false);
      setAssignClientTarget(null);
      fetchClients();
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në caktimin e moderatorëve');
    } finally {
      setAssignModeratorLoading(false);
    }
  };

  const handleCashTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Vendos një shumë të vlefshme');
      return;
    }
    if (!topupSiteId) {
      toast.error('Zgjidh stacionin ku do të kreditohet balanca');
      return;
    }
    
    try {
      setTopupLoading(true);
      if (isBusiness) {
        const response = await api.post(`/v1/business/employees/${selectedClient.id}/allocate`, {
          employee_id: selectedClient.id,
          site_id: topupSiteId,
          amount_lek: amount,
          reason: topupNotes || 'Alokim nga biznesi'
        });
        toast.success(`Punonjësi u kreditua me ${amount} Kredite. Bilanci i ri: ${response?.data?.new_employee_balance ?? amount} Kredite`);
      } else {
        const response = await api.post('/wallet/cash-topup', {
          user_id: selectedClient.id,
          amount: amount,
          site_id: topupSiteId,
          notes: topupNotes || 'Cash mbushje nga admin'
        });
        toast.success(`Balanca u rrit me ${amount} Kredite. Balanca totale e re: ${response.data.total_display_balance} Kredite`);
      }
      setTopupDialogOpen(false);
      setTopupAmount('');
      setTopupNotes('');
      // Refresh client details if open
      if (clientDetails) {
        // Use employee_record_id (emp.id) for re-fetch since API expects employee record id
        const refetchId = clientDetails._employee_record_id || clientDetails.employee_record_id || selectedClient.id;
        fetchClientDetails(refetchId);
      }
      // Refresh client list
      fetchClients();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim në mbushjen e balancës'));
    } finally {
      setTopupLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice?.id) {
      toast.error('Fatura nuk është e vlefshme për shkarkim');
      return;
    }

    try {
      const response = await api.get(`/admin/invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number || invoice.id || 'fatura'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: open full invoice preview and allow local PDF generation
      setSelectedInvoice(invoice);
      setInvoicePreviewOpen(true);
      toast.error(error.response?.data?.detail || 'PDF në server nuk u gjet. Mund ta gjenerosh nga "Shiko të plotë".');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!invoice?.id) {
      toast.error('Fatura nuk është e vlefshme për fshirje');
      return;
    }

    const confirmed = window.confirm('A je i sigurt që dëshiron të fshish këtë faturë?');
    if (!confirmed) return;

    try {
      await api.delete(`/admin/invoices/${invoice.id}`);
      setClientInvoices((prev) => prev.filter((item) => item.id !== invoice.id));
      if (selectedInvoice?.id === invoice.id) {
        setInvoicePreviewOpen(false);
        setSelectedInvoice(null);
      }
      toast.success('Fatura u fshi me sukses');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Gabim gjatë fshirjes së faturës'));
    }
  };

  const generateInvoiceNumber = (invoice) => {
    if (!invoice) return 'N/A';
    if (invoice.invoice_number) return invoice.invoice_number;
    const sourceDate = invoice.created_at || invoice.start_time || new Date().toISOString();
    const date = new Date(sourceDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const id = (invoice.id || 'XXXXXX').slice(0, 6).toUpperCase();
    return `ALB-${year}${month}-${id}`;
  };

  const formatEuropeanDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const openInvoicePreview = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoicePreviewOpen(true);
  };

  const handleGenerateInvoicePDF = async () => {
    if (!invoiceRef.current || !selectedInvoice) return;

    try {
      setInvoicePdfLoading(true);
      await saveElementAsPdf(invoiceRef.current, `Fatura-${generateInvoiceNumber(selectedInvoice)}.pdf`);
      toast.success('Fatura u gjenerua dhe u shkarkua me sukses');
    } catch (error) {
      toast.error('Gabim në gjenerimin e PDF');
    } finally {
      setInvoicePdfLoading(false);
    }
  };

  const normalizeStatusLabel = (status) => {
    if (!status) return '-';
    const value = String(status).toLowerCase();
    if (value === 'completed') return 'Përfunduar';
    if (value === 'stopped') return 'Ndalur';
    if (value === 'active') return 'Aktiv';
    if (value === 'pending') return 'Në pritje';
    if (value === 'paid') return 'Paguar';
    if (value === 'failed') return 'Dështuar';
    return status;
  };

  const getInvoiceStatusLabel = (invoice) => {
    if (!invoice) return '-';
    if (invoice.status) return normalizeStatusLabel(invoice.status);

    const type = String(invoice.type || '').toLowerCase();
    const invoiceType = String(invoice.invoice_type || '').toLowerCase();
    const mergedType = `${type} ${invoiceType}`;
    if (['charging', 'session', 'plug_charge'].includes(type)) return 'Përfunduar';
    if (
      ['cash_topup', 'cash_recharge', 'online_topup'].includes(type) ||
      mergedType.includes('business_credit') ||
      mergedType.includes('business_employee_allocation')
    ) return 'Paguar';
    return '-';
  };

  const isCashTopupInvoice = (invoice) => {
    const mergedType = `${String(invoice?.type || '').toLowerCase()} ${String(invoice?.invoice_type || '').toLowerCase()}`;
    return (
      mergedType.includes('cash_topup') ||
      mergedType.includes('cash_recharge') ||
      mergedType.includes('online_topup') ||
      mergedType.includes('business_credit') ||
      mergedType.includes('business_employee_allocation')
    );
  };

  const getInvoiceCurrency = (invoice) => (isCashTopupInvoice(invoice) ? 'LEK' : 'Kredite');

  const completedSessions = clientSessions.filter((session) => {
    const value = String(session?.status || '').toLowerCase();
    return ['completed', 'stopped', 'finished'].includes(value);
  });

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-primary" /> 
      : <ArrowDown className="w-4 h-4 ml-1 text-primary" />;
  };

  const getStatusBadge = (status) => {
    const isActive = status === 'active' || status === true;
    return isActive ? (
      <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
        <CheckCircle className="w-3 h-3 mr-1" /> Aktiv
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
        <Ban className="w-3 h-3 mr-1" /> Bllokuar
      </Badge>
    );
  };

  const getAccountTypeBadge = (type) => {
    const isBusiness = type === 'business';
    return isBusiness ? (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-violet-950 text-violet-300 border border-violet-800">
        <Building2 className="w-3 h-3" /> Biznes
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-slate-800 text-slate-300 border border-slate-700">
        <User className="w-3 h-3" /> Individ
      </span>
    );
  };

  const getVerifiedBadge = (isVerified) => {
    return isVerified ? (
      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
        <ShieldCheck className="w-3 h-3 mr-1" /> Po
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
        <ShieldX className="w-3 h-3 mr-1" /> Jo
      </Badge>
    );
  };

  const getModeratorNames = (client) => getClientModeratorNames(client, moderators);

  // Helper function to get verification status and missing documents
  const getVerificationStatus = (client) => {
    if (!client) return { isVerified: false, missing: [] };
    
    const missing = [];
    
    // Check OTP verification
    if (!client.is_verified && !client.email_verified) {
      missing.push('Email i pa verifikuar me OTP');
    }
    
    // Check ID Card
    if (!client.id_card && !client.id_card_front && !client.id_card_back) {
      missing.push('Karta e Identitetit mungon');
    }
    
    // Check NIPT for business accounts
    if (client.account_type === 'business' && !client.nipt) {
      missing.push('NIPT mungon (llogari biznesi)');
    }
    
    // Check phone verification
    if (!client.phone_verified && !client.phone) {
      missing.push('Numri i telefonit mungon');
    }
    
    const isFullyVerified = missing.length === 0 && client.is_verified;
    
    return { isVerified: isFullyVerified, missing };
  };

  const VerificationWarningBadge = ({ client }) => {
    const { isVerified, missing } = getVerificationStatus(client);
    
    if (isVerified) return null;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className="bg-amber-500/10 text-amber-500 border-amber-500/20 cursor-help ml-2"
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> Pa verifikuar
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium text-amber-500">Dokumente që mungojnë:</p>
              <ul className="text-xs space-y-1">
                {missing.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-1">
                    <X className="w-3 h-3 text-red-400" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const SortableHeader = ({ field, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {children}
        {getSortIcon(field)}
      </div>
    </TableHead>
  );

  // Actions dropdown for each client row
  const ClientActions = ({ client }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-actions-${client.id}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleViewDetails(client)}>
          <Eye className="w-4 h-4 mr-2" /> Shiko Detajet
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
          <UserCircle className="w-4 h-4 mr-2" /> Profili i Plotë
        </DropdownMenuItem>
          {canAssignModerator && !isBusiness && (
            <DropdownMenuItem onClick={() => openAssignModeratorDialog(client)}>
              <Link2 className="w-4 h-4 mr-2" /> Cakto Moderatorë
            </DropdownMenuItem>
          )}
        <DropdownMenuItem onClick={() => openPasswordDialog(client)} className="text-amber-500">
          <Key className="w-4 h-4 mr-2" /> Ndrysho Fjalëkalimin
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openEmailDialog(client)} className="text-blue-500">
          <Mail className="w-4 h-4 mr-2" /> Ndrysho Email-in
        </DropdownMenuItem>
        {(client.status === 'active' || client.is_active) ? (
          <DropdownMenuItem onClick={() => handleBlock(client.id)} className="text-red-500">
            <Ban className="w-4 h-4 mr-2" /> Blloko Klientin
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => handleUnblock(client.id)} className="text-green-500">
            <CheckCircle className="w-4 h-4 mr-2" /> Zhblloko Klientin
          </DropdownMenuItem>
        )}
        {!isModerator && (
          <DropdownMenuItem onClick={() => openDeleteDialog(client)} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" /> Fshi Klientin
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-outfit flex items-center gap-2">
            {isBusiness ? (
              <>
                <Users className="w-7 h-7 text-emerald-500" />
                Punonjësit
              </>
            ) : (
              <>
                <UserCircle className="w-7 h-7 text-primary" />
                Klientët
              </>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isBusiness 
              ? 'Punonjësit e alokuar në bizneset tuaj'
              : 'Klientët e aplikacionit mobile'
            }
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Card className="px-4 py-2">
            <div className="text-sm text-muted-foreground">
              {isBusiness ? 'Total Punonjës' : 'Total Klientë'}
            </div>
            <div className="text-2xl font-bold text-foreground">{total}</div>
          </Card>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="create-client-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {isBusiness ? 'Shto Punonjës' : 'Shto Klient'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={isBusiness ? "Kërko punonjës..." : "Kërko me emër, email ose telefon..."}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="client-search-input"
              />
            </div>
            {!isBusiness && (
              <>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[140px]" data-testid="client-status-filter">
                    <SelectValue placeholder="Statusi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjithë</SelectItem>
                    <SelectItem value="active">Aktivë</SelectItem>
                    <SelectItem value="blocked">Bllokuar</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={verifiedFilter} onValueChange={(v) => { setVerifiedFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[140px]" data-testid="client-verified-filter">
                    <SelectValue placeholder="Verifikuar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjithë</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">
            {isBusiness ? 'Lista e Punonjësve' : 'Lista e Klientëve'}
          </CardTitle>
          <CardDescription>
            {clients.length} {isBusiness ? 'punonjës' : 'klientë'} të shfaqur nga {total} total • Faqja {currentPage} nga {totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">#</TableHead>
                      <SortableHeader field="full_name">Emri</SortableHeader>
                      <TableHead>ID / NIPT</TableHead>
                      <SortableHeader field="wallet_balance">Balanca</SortableHeader>
                      <SortableHeader field="charging_discount_percent">Zbritja</SortableHeader>
                      <SortableHeader field="total_sessions">Seanca</SortableHeader>
                      <SortableHeader field="status">Statusi</SortableHeader>
                      <SortableHeader field="is_verified">Verifikuar</SortableHeader>
                      <SortableHeader field="account_type">Tipi</SortableHeader>
                      <SortableHeader field="created_at">Regjistruar</SortableHeader>
                      <TableHead className="w-12 text-right">⋮</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client, index) => (
                      <TableRow 
                        key={client.id} 
                        data-testid={`client-row-${client.id}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <TableCell className="text-muted-foreground font-mono text-sm">
                          {total - ((currentPage - 1) * ITEMS_PER_PAGE) - index}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{client.full_name}</div>
                            <div className="text-xs text-muted-foreground">{client.email}</div>
                            {!isBusiness && getClientModeratorIds(client).length > 0 && (
                              <div className="text-[11px] text-blue-600 mt-0.5">
                                Moderatorë: {getModeratorNames(client).join(', ') || 'Të paidentifikuar'}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.account_type === 'business' && client.nipt ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Building2 className="w-3 h-3 text-violet-400" />
                              <span className="font-mono">{client.nipt}</span>
                            </div>
                          ) : client.id_card ? (
                            <div className="flex items-center gap-1 text-sm">
                              <IdCard className="w-3 h-3 text-slate-400" />
                              <span className="font-mono">{client.id_card}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium">{formatCurrency(client.wallet_balance || 0, '')}</span>
                            <span className="text-muted-foreground text-xs ml-1">Kredite</span>
                            <div className="text-[11px] text-muted-foreground">Balanca totale</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.charging_discount_percent !== undefined && client.charging_discount_percent !== null ? (
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex w-fit items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600">
                                {Number(client.charging_discount_percent).toFixed(2).replace(/\.00$/, '')}%
                              </span>
                              {client.charging_discount_fixed_lek !== undefined && client.charging_discount_fixed_lek !== null && (
                                <span className="inline-flex w-fit items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-600">
                                  {Number(client.charging_discount_fixed_lek).toFixed(2).replace(/\.00$/, '')} Kredite/kWh
                                </span>
                              )}
                            </div>
                          ) : client.charging_discount_fixed_lek !== undefined && client.charging_discount_fixed_lek !== null ? (
                            <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-600">
                              {Number(client.charging_discount_fixed_lek).toFixed(2).replace(/\.00$/, '')} Kredite/kWh
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {client.total_sessions || 0}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(client.status || client.is_active)}
                        </TableCell>
                        <TableCell>
                          {getVerifiedBadge(client.is_verified)}
                        </TableCell>
                        <TableCell>
                          {getAccountTypeBadge(client.account_type)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(client.created_at, { includeTime: false })}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <ClientActions client={client} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {clients.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                          Nuk u gjetën klientë
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Duke shfaqur {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, total)} nga {total}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Para
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="next-page-btn"
                    >
                      Pas <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Report Section - Always visible at bottom */}
      <div className="space-y-4" data-testid="clients-report">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Raporti i Klientëve
          </h2>
          {reportLoading && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
              Duke përditësuar...
            </span>
          )}
        </div>
        
        {report ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" /> Total
                </div>
                <div className="text-2xl font-bold mt-1">{report.summary?.total_clients || 0}</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Aktivë
                </div>
                <div className="text-2xl font-bold mt-1 text-green-500">{report.summary?.active_clients || 0}</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Ban className="w-4 h-4 text-red-500" /> Bllokuar
                </div>
                <div className="text-2xl font-bold mt-1 text-red-500">{report.summary?.blocked_clients || 0}</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" /> Verifikuar
                </div>
                <div className="text-2xl font-bold mt-1 text-emerald-500">{report.summary?.verified_clients || 0}</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4 text-slate-400" /> Individë
                </div>
                <div className="text-xl font-bold mt-1">{report.account_types?.individual || 0} <span className="text-sm font-normal text-muted-foreground">({report.account_types?.individual_percentage || 0}%)</span></div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="w-4 h-4 text-violet-400" /> Biznese
                </div>
                <div className="text-xl font-bold mt-1">{report.account_types?.business || 0} <span className="text-sm font-normal text-muted-foreground">({report.account_types?.business_percentage || 0}%)</span></div>
              </Card>
            </div>

            {/* Top Lists */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Top by Sessions */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" /> Top 5 me Shumë Seanca
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {report.top_by_sessions?.length > 0 ? (
                    report.top_by_sessions.map((client, idx) => (
                      <div key={client.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-primary">{idx + 1}.</span>
                          <span className="truncate max-w-[120px]">{client.full_name}</span>
                        </div>
                        <Badge variant="secondary">{client.total_sessions} seanca</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Nuk ka të dhëna</p>
                  )}
                </CardContent>
              </Card>

              {/* Top by Balance */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-emerald-500" /> Top 5 me Shumë Balancë
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {report.top_by_balance?.length > 0 ? (
                    report.top_by_balance.map((client, idx) => (
                      <div key={client.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-emerald-500">{idx + 1}.</span>
                          <span className="truncate max-w-[120px]">{client.full_name}</span>
                        </div>
                        <Badge variant="secondary">{formatCurrency(client.wallet_balance || 0, '')} Kredite</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Nuk ka të dhëna</p>
                  )}
                </CardContent>
              </Card>

              {/* Top by Energy */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" /> Top 5 me Shumë Energji
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {report.top_by_energy?.length > 0 ? (
                    report.top_by_energy.map((client, idx) => (
                      <div key={client.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-amber-500">{idx + 1}.</span>
                          <span className="truncate max-w-[120px]">{client.full_name}</span>
                        </div>
                        <Badge variant="secondary">{(client.total_energy_kwh || 0).toFixed(1)} kWh</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Nuk ka të dhëna</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top-up Stats & Registration Chart */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary" /> Statistikat e Rimbushjeve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Total Rimbushje</div>
                      <div className="text-xl font-bold">{report.topup_stats?.total_topups || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Shuma Totale</div>
                      <div className="text-xl font-bold">{formatCurrency(report.topup_stats?.total_amount || 0, '')} Kredite</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Mesatarja</div>
                      <div className="text-xl font-bold">{formatCurrency(report.topup_stats?.avg_amount || 0, '')} Kredite</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <PieChart className="w-4 h-4 text-primary" /> Metodat e Regjistrimit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {report.auth_providers?.map((provider, idx) => (
                      <Badge key={`${provider.provider}-${idx}`} variant="outline" className="text-sm">
                        {provider.provider === 'email' ? 'Email' : 
                         provider.provider === 'google' ? 'Google' :
                         provider.provider === 'facebook' ? 'Facebook' :
                         provider.provider === 'apple' ? 'Apple' : provider.provider}
                        : {provider.count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Registration by Month */}
            {report.registrations_by_month?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" /> Regjistrimet sipas Muajit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-2 h-32 overflow-x-auto pb-2">
                    {report.registrations_by_month.map((item) => {
                      const maxCount = Math.max(...report.registrations_by_month.map(r => r.count));
                      const heightPercent = (item.count / maxCount) * 100;
                      return (
                        <div key={item.month} className="flex flex-col items-center min-w-[60px]">
                          <div 
                            className="w-10 bg-primary/80 rounded-t transition-all hover:bg-primary"
                            style={{ height: `${heightPercent}%`, minHeight: '4px' }}
                            title={`${item.count} regjistrime`}
                          />
                          <span className="text-xs text-muted-foreground mt-1">{item.month}</span>
                          <span className="text-xs font-medium">{item.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {/* Detail Dialog - Redesigned */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-primary" />
              Profili i Klientit
              {clientDetails && <VerificationWarningBadge client={clientDetails} />}
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : clientDetails ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Përmbledhje</TabsTrigger>
                <TabsTrigger value="documents">Dokumentat</TabsTrigger>
                <TabsTrigger value="sessions">Sesionet ({completedSessions.length || clientDetails?.total_sessions || 0})</TabsTrigger>
                <TabsTrigger value="transactions">Transaksionet</TabsTrigger>
                <TabsTrigger value="invoices">Faturat ({clientInvoices.length || 0})</TabsTrigger>
              </TabsList>
              
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Header with name and status */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                      <UserCircle className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold">{clientDetails.full_name}</h2>
                        {getAccountTypeBadge(clientDetails.account_type)}
                      </div>
                      <p className="text-muted-foreground">{clientDetails.email}</p>
                      {clientDetails.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {clientDetails.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(clientDetails.status || clientDetails.is_active)}
                    <p className="text-xs text-muted-foreground mt-1">
                      Regjistruar: {formatDate(clientDetails.created_at, { includeTime: false })}
                    </p>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4 bg-emerald-500/10 border-emerald-500/20">
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <Wallet className="w-4 h-4" /> Balanca
                    </div>
                    <div className="text-2xl font-bold mt-1 text-emerald-500">
                      {formatCurrency(clientDetails.wallet_balance || 0, '')} <span className="text-sm font-normal">Kredite</span>
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-emerald-700 dark:text-emerald-300">
                      <div className="flex items-center justify-between gap-2">
                        <span>Globale</span>
                        <span className="font-medium">{formatCurrency(getWalletSummary(clientDetails).globalBalance, '')} Kredite</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span>Për stacione</span>
                        <span className="font-medium">{formatCurrency(getWalletSummary(clientDetails).stationBalance, '')} Kredite</span>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Activity className="w-4 h-4" /> Seanca
                    </div>
                    <div className="text-2xl font-bold mt-1">
                      {clientDetails.total_sessions || 0}
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Zap className="w-4 h-4" /> Energji
                    </div>
                    <div className="text-2xl font-bold mt-1">
                      {(clientDetails.total_energy_kwh || 0).toFixed(1)} <span className="text-sm font-normal">kWh</span>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="w-4 h-4" /> Shpenzuar
                    </div>
                    <div className="text-2xl font-bold mt-1">
                      {formatCurrency(clientDetails.total_spent_lek || 0, '')} <span className="text-sm font-normal">Kredite</span>
                    </div>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-emerald-500" /> Ndarja e Balancës
                    </CardTitle>
                    <CardDescription>
                      Totali i klientit, balanca globale dhe kreditë aktive sipas stacioneve.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-lg border bg-muted/30 p-3">
                        <div className="text-xs text-muted-foreground uppercase">Totali</div>
                        <div className="text-lg font-semibold mt-1">{formatCurrency(getWalletSummary(clientDetails).total, '')} Kredite</div>
                      </div>
                      <div className="rounded-lg border bg-muted/30 p-3">
                        <div className="text-xs text-muted-foreground uppercase">Balanca e përgjithshme</div>
                        <div className="text-lg font-semibold mt-1">{formatCurrency(getWalletSummary(clientDetails).globalBalance, '')} Kredite</div>
                      </div>
                      <div className="rounded-lg border bg-muted/30 p-3">
                        <div className="text-xs text-muted-foreground uppercase">Balanca për stacione</div>
                        <div className="text-lg font-semibold mt-1">{formatCurrency(getWalletSummary(clientDetails).stationBalance, '')} Kredite</div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium mb-2">Kredite aktive sipas stacioneve</div>
                      {getWalletSummary(clientDetails).restrictedWallets.length > 0 ? (
                        <div className="space-y-2">
                          {getWalletSummary(clientDetails).restrictedWallets.map((wallet, index) => (
                            <div key={`${wallet.site_id || wallet.moderator_id || 'station'}-${index}`} className="flex items-center justify-between rounded-lg border p-3">
                              <div>
                                <div className="font-medium">{wallet.site_name || wallet.site_id || 'Stacion i dedikuar'}</div>
                                <div className="text-xs text-muted-foreground">Balancë e kufizuar për këtë stacion</div>
                              </div>
                              <div className="font-semibold text-emerald-600">{formatCurrency(wallet.balance || 0, '')} Kredite</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                          Klienti nuk ka kredi aktive të lidhura me stacione specifike.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Personal Info Grid */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Informacione Personale</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {clientDetails.id_card && (
                        <div>
                          <span className="text-muted-foreground">Nr. Kartës ID</span>
                          <p className="font-medium">{clientDetails.id_card}</p>
                        </div>
                      )}
                      {clientDetails.nipt && (
                        <div>
                          <span className="text-muted-foreground">NIPT</span>
                          <p className="font-medium">{clientDetails.nipt}</p>
                        </div>
                      )}
                      {clientDetails.country && (
                        <div>
                          <span className="text-muted-foreground">Shteti</span>
                          <p className="font-medium">{clientDetails.country}</p>
                        </div>
                      )}
                      {clientDetails.city && (
                        <div>
                          <span className="text-muted-foreground">Qyteti</span>
                          <p className="font-medium">{clientDetails.city}</p>
                        </div>
                      )}
                      {clientDetails.address && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Adresa</span>
                          <p className="font-medium">{clientDetails.address}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Login i Fundit</span>
                        <p className="font-medium">
                          {clientDetails.last_login 
                            ? formatDate(clientDetails.last_login, { includeTime: true })
                            : 'Asnjëherë'
                          }
                        </p>
                      </div>
                      {clientDetails.last_login_method && (
                        <div>
                          <span className="text-muted-foreground">Metoda Login</span>
                          <p className="font-medium">
                            {clientDetails.last_login_method === 'face_id' ? 'Face ID' : 
                             clientDetails.last_login_method === 'touch_id' ? 'Touch ID' : 'Email/Password'}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {canManageDiscount && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Zbritja e Karikimit për Klientin</CardTitle>
                      <CardDescription>
                        Vendos zbritje me përqindje, me vlerë fikse në Kredite/kWh, ose të dyja.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="w-full md:w-56">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="Zbritja %"
                            value={discountDraft}
                            onChange={(e) => setDiscountDraft(e.target.value)}
                          />
                        </div>
                        <div className="w-full md:w-56">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Zbritja fikse Kredite/kWh"
                            value={discountFixedDraft}
                            onChange={(e) => setDiscountFixedDraft(e.target.value)}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={handleSaveClientDiscount} disabled={discountSaving}>
                            {discountSaving ? 'Duke ruajtur...' : 'Ruaj Zbritjen'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setDiscountDraft('');
                              setDiscountFixedDraft('');
                            }}
                            disabled={discountSaving}
                          >
                            Pastro
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Zbritja aktuale: {clientDetails?.charging_discount_percent ?? 0}%
                        {clientDetails?.charging_discount_fixed_lek !== undefined && clientDetails?.charging_discount_fixed_lek !== null
                          ? ` • ${clientDetails.charging_discount_fixed_lek} Kredite/kWh`
                          : ''}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
                  <Button 
                    variant="default" 
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openTopupDialog(clientDetails);
                    }}
                  >
                    <Banknote className="w-4 h-4 mr-2" /> Rimbush Cash
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openPasswordDialog(clientDetails);
                    }}
                  >
                    <Key className="w-4 h-4 mr-2" /> Ndrysho Fjalëkalimin
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openEmailDialog(clientDetails);
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" /> Ndrysho Email-in
                  </Button>
                  {(clientDetails.status === 'active' || clientDetails.is_active) ? (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleBlock(clientDetails.id)}
                    >
                      <Ban className="w-4 h-4 mr-2" /> Blloko
                    </Button>
                  ) : (
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => handleUnblock(clientDetails.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Zhblloko
                    </Button>
                  )}
                </div>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Dokumentat e Ngarkuara
                    </CardTitle>
                    <CardDescription>
                      Dokumentat e identifikimit të klientit
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* ID Card */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <IdCard className="w-4 h-4" /> Karta ID
                          </span>
                          {clientDetails.id_card_document_url ? (
                            <Badge className="bg-green-500/20 text-green-500">Ngarkuar</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-500">Mungon</Badge>
                          )}
                        </div>
                        {clientDetails.id_card_document_url ? (
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}/api${clientDetails.id_card_document_url}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}/api${clientDetails.id_card_document_url}`}
                              alt="ID Card" 
                              className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling?.classList.remove('hidden');
                              }}
                            />
                            <div className="hidden w-full h-32 bg-muted rounded flex items-center justify-center text-sm text-muted-foreground">
                              Kliko për të hapur dokumentin
                            </div>
                          </a>
                        ) : (
                          <div className="w-full h-32 bg-muted rounded flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground/50" />
                          </div>
                        )}
                        {clientDetails.id_card_verified && (
                          <Badge className="mt-2 bg-green-500/20 text-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" /> Verifikuar
                          </Badge>
                        )}
                      </div>

                      {/* NIPT Document */}
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4" /> NIPT / Çertifikatë Biznesi
                          </span>
                          {clientDetails.nipt_document_url ? (
                            <Badge className="bg-green-500/20 text-green-500">Ngarkuar</Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-500">Mungon</Badge>
                          )}
                        </div>
                        {clientDetails.nipt_document_url ? (
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}/api${clientDetails.nipt_document_url}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block"
                          >
                            {clientDetails.nipt_document_url.endsWith('.pdf') ? (
                              <div className="w-full h-32 bg-muted rounded flex items-center justify-center border hover:bg-muted/80 transition">
                                <div className="text-center">
                                  <FileText className="w-8 h-8 mx-auto text-primary" />
                                  <span className="text-sm text-muted-foreground mt-2">Kliko për PDF</span>
                                </div>
                              </div>
                            ) : (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api${clientDetails.nipt_document_url}`}
                                alt="NIPT Document" 
                                className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition"
                              />
                            )}
                          </a>
                        ) : (
                          <div className="w-full h-32 bg-muted rounded flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground/50" />
                          </div>
                        )}
                        {clientDetails.nipt_verified && (
                          <Badge className="mt-2 bg-green-500/20 text-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" /> Verifikuar
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Business Documents */}
                    {clientDetails.client_type === 'business' && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <Building2 className="w-4 h-4" /> Dokumentat e Biznesit
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">NIPT</span>
                            <p className="font-medium">{clientDetails.nipt || 'Mungon'}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Emri Biznesit</span>
                            <p className="font-medium">{clientDetails.business_name || clientDetails.full_name}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* No documents message */}
                    {!clientDetails.id_card_document_url && !clientDetails.nipt_document_url && (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Ky klient nuk ka ngarkuar dokumente</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Sessions Tab */}
              <TabsContent value="sessions" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="w-4 h-4" /> Seancat e Karikimit
                    </CardTitle>
                    <CardDescription>
                      {clientDetails.total_sessions || 0} seanca totale
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {completedSessions?.length > 0 ? (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {completedSessions.map((session) => (
                          <div key={session.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <Zap className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{session.charger_id}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(session.start_time, { includeTime: true })}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{(session.energy_kwh || 0).toFixed(2)} kWh</p>
                              <p className="text-sm text-emerald-500">
                                {formatCurrency(session.cost_lek || 0, '')} Kredite
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">{normalizeStatusLabel(session.status)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Ky klient nuk ka seanca karikimi</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transactions Tab */}
              <TabsContent value="transactions" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Wallet className="w-4 h-4" /> Transaksionet
                    </CardTitle>
                    <CardDescription>
                      Historiku i transaksioneve të portofolit
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {clientDetails.recent_transactions?.length > 0 ? (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {clientDetails.recent_transactions.map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup' 
                                  ? 'bg-emerald-500/20' 
                                  : 'bg-red-500/20'
                              }`}>
                                {tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup' ? (
                                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                                ) : (
                                  <Zap className="w-5 h-5 text-red-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{tx.description || tx.type}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(tx.created_at, { includeTime: true })}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-medium ${
                                tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup'
                                  ? 'text-emerald-500' 
                                  : 'text-red-500'
                              }`}>
                                {tx.type === 'cash_topup' || tx.type === 'cash_recharge' || tx.type === 'online_topup' ? '+' : '-'}
                                {formatCurrency(tx.amount || 0, '')} Kredite
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Balancë: {formatCurrency(tx.balance_after || 0, '')} Kredite
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Wallet className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Nuk ka transaksione</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Faturat
                    </CardTitle>
                    <CardDescription>
                      Faturat e klientit për rimbushje dhe karikime
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {clientInvoices?.length > 0 ? (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {clientInvoices.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm hover:bg-muted transition">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{inv.invoice_number}</p>
                                <p className="text-xs text-muted-foreground">
                                  {isCashTopupInvoice(inv) ? 'Rimbushje Cash' : `Statusi: ${getInvoiceStatusLabel(inv)}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {inv.license_plate ? `Targa: ${inv.license_plate}` : 'Pa targë'}
                                  {inv.charger_id ? ` • Charger: ${inv.charger_id}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex flex-col items-end gap-1">
                              <p className="font-medium text-emerald-500">
                                {formatCurrency(inv.amount || 0, getInvoiceCurrency(inv))}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(inv.created_at, { includeTime: false })}
                              </p>
                              <p className="text-xs text-muted-foreground">{getInvoiceStatusLabel(inv)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 min-w-[140px]">
                              <span className="text-[11px] text-muted-foreground uppercase tracking-wide">Veprime</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 w-full"
                                onClick={() => openInvoicePreview(inv)}
                              >
                                <Eye className="w-3 h-3 mr-1" /> Shiko Faturën
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 w-full"
                                onClick={() => handleDownloadInvoice(inv)}
                              >
                                <Download className="w-3 h-3 mr-1" /> Shkarko PDF
                              </Button>
                              {user?.role === 'ADMIN' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 w-full text-red-500 border-red-500/30 hover:bg-red-500/10"
                                  onClick={() => handleDeleteInvoice(inv)}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" /> Fshi
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Nuk ka fatura</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>

      <InvoicePopup
        session={invoicePreviewOpen ? selectedInvoice : null}
        onClose={() => {
          setInvoicePreviewOpen(false);
          setSelectedInvoice(null);
        }}
      />

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" /> Cakto Moderatorë
            </DialogTitle>
            <DialogDescription>
              Zgjidh një ose më shumë moderatorë për {assignClientTarget?.full_name || 'klientin e zgjedhur'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 max-h-[320px] overflow-y-auto">
            {moderators.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nuk ka moderatorë të disponueshëm.</p>
            ) : (
              moderators.map((moderator) => (
                <div
                  key={moderator.id}
                  className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 cursor-pointer"
                  onClick={() => toggleModeratorSelection(moderator.id)}
                >
                  <Checkbox
                    checked={selectedModeratorIds.includes(moderator.id)}
                    onCheckedChange={() => toggleModeratorSelection(moderator.id)}
                    data-testid={`assign-moderator-${moderator.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{moderator.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{moderator.email}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssignDialogOpen(false);
                setAssignClientTarget(null);
              }}
              disabled={assignModeratorLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleAssignModerator}
              disabled={assignModeratorLoading || !assignClientTarget}
              data-testid="assign-moderator-save"
            >
              {assignModeratorLoading
                ? 'Duke ruajtur...'
                : `Ruaj (${selectedModeratorIds.length} moderatorë)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Topup Dialog */}
      <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-500" />
              {isBusiness ? 'Aloko Balancë' : 'Rimbush Cash'}
            </DialogTitle>
            <DialogDescription>
              {isBusiness
                ? `Aloko balancë për ${selectedClient?.full_name} nga portofoli i biznesit`
                : `Shto balancë për ${selectedClient?.full_name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedClient && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Balanca aktuale:</span>
                  <span className="font-bold text-emerald-500">
                    {formatCurrency(selectedClient.wallet_balance || 0, '')} Kredite
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Stacioni</label>
              <Select value={topupSiteId} onValueChange={setTopupSiteId}>
                <SelectTrigger data-testid="topup-site-select">
                  <SelectValue placeholder="Zgjidh stacionin" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name || site.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Kjo mbushje cash përdoret vetëm në stacionin e zgjedhur.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Shuma (Kredite)</label>
              <Input
                type="number"
                placeholder="p.sh. 500"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                min="1"
                data-testid="topup-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Shënime (opsionale)</label>
              <Input
                type="text"
                placeholder="p.sh. Pagesë cash në zyrë"
                value={topupNotes}
                onChange={(e) => setTopupNotes(e.target.value)}
                data-testid="topup-notes-input"
              />
            </div>

            {topupAmount && parseFloat(topupAmount) > 0 && selectedClient && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Balanca totale e re:</span>
                  <span className="font-bold text-emerald-500">
                    {formatCurrency((selectedClient.wallet_balance || 0) + parseFloat(topupAmount), '')} Kredite
                  </span>
                </div>
                <p className="text-xs text-emerald-300 mt-2">
                  {isBusiness
                    ? `Alokimi kryhet për stacionin: ${sites.find((site) => site.id === topupSiteId)?.name || 'stacionin e zgjedhur'}`
                    : `Krediti lidhet me: ${sites.find((site) => site.id === topupSiteId)?.name || 'stacionin e zgjedhur'}`}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTopupDialogOpen(false)}
              disabled={topupLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleCashTopup}
              disabled={topupLoading || !topupAmount || parseFloat(topupAmount) <= 0 || !topupSiteId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {topupLoading ? 'Duke procesuar...' : (isBusiness ? 'Konfirmo Alokimin' : 'Konfirmo Mbushjen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Ndrysho Fjalëkalimin
            </DialogTitle>
            <DialogDescription>
              Vendos fjalëkalimin e ri për {selectedClient?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Fjalëkalimi i Ri</label>
              <Input
                type="password"
                placeholder="Minimum 6 karaktere"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordDialogOpen(false)}
              disabled={passwordLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={passwordLoading || !newPassword}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {passwordLoading ? 'Duke ruajtur...' : 'Ndrysho Fjalëkalimin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Ndrysho Email-in
            </DialogTitle>
            <DialogDescription>
              Ndrysho email-in për {selectedClient?.full_name || selectedClient?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email aktual</label>
              <Input value={selectedClient?.email || ''} disabled />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email i ri</label>
              <Input
                type="email"
                placeholder="klient@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                data-testid="new-email-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              disabled={emailLoading}
            >
              Anulo
            </Button>
            <Button
              onClick={handleChangeEmail}
              disabled={emailLoading || !newEmail || newEmail.trim().toLowerCase() === (selectedClient?.email || '').toLowerCase()}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {emailLoading ? 'Duke ruajtur...' : 'Ndrysho Email-in'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Fshi Klientin
            </DialogTitle>
            <DialogDescription>
              Jeni të sigurt që dëshironi të fshini këtë klient?
            </DialogDescription>
          </DialogHeader>
          
          {clientToDelete && (
            <div className="py-4 space-y-2">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="font-medium text-foreground">{clientToDelete.full_name}</p>
                <p className="text-sm text-muted-foreground">{clientToDelete.email}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Balanca: {formatCurrency(clientToDelete.wallet_balance || 0)}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400">
                ⚠️ Kjo veprim është i pakthyeshëm. Të gjitha të dhënat e klientit do të fshihen përgjithmonë.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteLoading}
            >
              Anulo
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Duke fshirë...' : 'Fshi Klientin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {isBusiness ? 'Shto Punonjës të Ri' : 'Shto Klient të Ri'}
            </DialogTitle>
            <DialogDescription>
              {isBusiness 
                ? 'Krijo një punonjës të ri që do të punojë me biznesen tuaj'
                : 'Krijo një klient të ri direkt nga paneli admin'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-foreground">Emri i Plotë *</label>
              <Input
                placeholder="p.sh. Artan Hoxha"
                value={createForm.full_name}
                onChange={e => setCreateForm(p => ({...p, full_name: e.target.value}))}
                className="mt-1"
                data-testid="create-client-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Email *</label>
              <Input
                type="email"
                placeholder="email@shembull.com"
                value={createForm.email}
                onChange={e => setCreateForm(p => ({...p, email: e.target.value}))}
                className="mt-1"
                data-testid="create-client-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Telefon</label>
              <Input
                placeholder="+355 6X XXX XXXX"
                value={createForm.phone}
                onChange={e => setCreateForm(p => ({...p, phone: e.target.value}))}
                className="mt-1"
                data-testid="create-client-phone"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Fjalëkalimi *</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder="Minimum 6 karaktere"
                  value={createForm.password}
                  onChange={e => setCreateForm(p => ({...p, password: e.target.value}))}
                  className="pl-9"
                  data-testid="create-client-password"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
              Anulo
            </Button>
            <Button onClick={handleCreateClient} disabled={createLoading} data-testid="create-client-submit">
              {createLoading ? (isBusiness ? 'Duke krijuar...' : 'Duke krijuar...') : (isBusiness ? 'Krijo Punonjësin' : 'Krijo Klientin')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
